import { useWelcomeFlowStore } from "@/stores/welcomeFlow/welcomeFlow.store";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { useFlowGuideCoordinationStore } from "@/stores/flowGuide/flowGuideCoordination.store";
import { waitForHasuraAuthenticated } from "@/router/router";
import { HasuraNotAuthenticatedError } from "@/stores/hasura/hasuraErrors";
import { useIsDesktop } from "@/composables/useIsDesktop";
import { waitForProjectsPageReady } from "@/pages/Projects/composables/useProjectsPageReadySignal";

import { WELCOME_FLOW_KEY, WELCOME_RELEASE_DATE } from "./welcomeFlow.config";
import { resolveWelcomeVariant } from "./resolveWelcomeVariant";
import { getWelcomeDeck, type WelcomeDeckId } from "./decks";
import {
  isWelcomeFlowDone,
  reportSwallowedFailure,
  insertWelcomeFlowRow,
  markWelcomeFlowCompleted,
  recordWelcomeFlowEvent,
  waitForModalStackToEmpty,
} from "./welcomeFlowShared";
import type { WelcomeStepReached } from "./types/WelcomeFlowTypes";

// Once per app session; App.vue's auth watcher re-runs on every route change.
let checked = false;

// Every nav ⓘ (ProjectNavigation.vue / MainNavigation.vue) replays through this one helper: closes
// whatever guide is already running (WelcomeTour registers itself as the active tour, so this also
// stops another welcome-tour deck if one is somehow still open), logs the replay against the deck's
// own flow key, then opens it on demand.
export function replayWelcomeDeck(deck: WelcomeDeckId): void {
  useFlowGuideCoordinationStore().closeActiveTour();
  const flowKey = getWelcomeDeck(deck).flowKey;
  if (flowKey) void recordWelcomeFlowEvent({ flowKey, event: "replayed" });
  useWelcomeFlowStore().openTour(deck, "existing", { onDemand: true });
}

export function useWelcomeFlow() {
  async function onTourFinished(result: "completed" | "skipped", stepReached: WelcomeStepReached | null = null): Promise<void> {
    const welcomeFlowStore = useWelcomeFlowStore();
    const wasOnDemand = welcomeFlowStore.isOnDemand;
    const finishedTour = welcomeFlowStore.activeTour;
    welcomeFlowStore.closeTour();
    // An on-demand run (e.g. the viewer guide's sidebar info-button path) is not the real
    // once-per-user flow, so it may never write anything here — even a completed run, and even for
    // a deck (the viewer) that ALSO auto-runs once per user through the ordinary path below.
    if (wasOnDemand || !finishedTour) return;

    // Belt-and-braces: a deck reachable only on demand would have no flow key at all.
    const flowKey = getWelcomeDeck(finishedTour.deck).flowKey;
    if (!flowKey) return;

    void recordWelcomeFlowEvent({ flowKey, event: result === "completed" ? "completed" : "abandoned", stepReached });

    if (result === "completed") await markWelcomeFlowCompleted(flowKey);
  }

  async function checkAndShow(): Promise<void> {
    if (checked) return;

    // The sidebar (and every anchor the tour targets) doesn't render below the desktop breakpoint.
    // Return without consuming the once-per-session guard, so a later check — after resizing up, or
    // on the user's next desktop session — still gets a chance to show the tour.
    if (!useIsDesktop().value) return;
    checked = true;

    try {
      await waitForHasuraAuthenticated();
      await waitForProjectsPageReady();
      await waitForModalStackToEmpty();

      // Mandatory, so "done" means completed rather than merely opened: a refresh mid-tour brings
      // it back on the next load, from the top, since no step position is stored anywhere.
      if (await isWelcomeFlowDone(WELCOME_FLOW_KEY, getWelcomeDeck("platform").mandatory === true)) return;

      const authStateStore = useAuthStateStore();
      const variant = resolveWelcomeVariant(authStateStore.user?.createdAt, WELCOME_RELEASE_DATE);

      await insertWelcomeFlowRow(WELCOME_FLOW_KEY, variant);
      void recordWelcomeFlowEvent({ flowKey: WELCOME_FLOW_KEY, event: "opened", variant });

      useWelcomeFlowStore().openTour("platform", variant);
    } catch (error) {
      if (error instanceof HasuraNotAuthenticatedError) return;
      reportSwallowedFailure("useWelcomeFlow.checkAndShow", error);
    }
  }

  return { checkAndShow, onTourFinished };
}
